/**
 * Navigation Configuration
 *
 * Defines role-based navigation menu structure.
 * Each role has its own navigation tree with appropriate routes and icons.
 *
 * Permission-based Navigation:
 * Navigation items can optionally include a `permission` or `permissions` property
 * to conditionally show/hide menu items based on user permissions.
 *
 * Example:
 * {
 *   id: 'users',
 *   label: 'Users',
 *   path: '/users',
 *   permission: 'users.view',  // Single permission
 *   // OR
 *   permissions: ['users.view', 'users.create'],  // Any of these
 *   requireAllPermissions: true,  // If true, requires ALL permissions
 * }
 */

import { ROLES } from './roles'

/**
 * Navigation configuration for Super Admin role
 * Organized in sections: MAIN, MANAGEMENT, SETTINGS
 */
const SUPER_ADMIN_NAVIGATION = [
  // MAIN Section
  {
    id: 'main-section',
    label: 'MAIN',
    isSection: true,
  },
  {
    id: 'dashboard',
    label: 'Dashboard',
    path: '/dashboard',
    icon: 'icon-grid',
    exact: true,
  },

  // MANAGEMENT Section
  {
    id: 'management-section',
    label: 'MANAGEMENT',
    isSection: true,
  },
  {
    id: 'companies',
    label: 'Companies',
    path: '/companies',
    icon: 'icon-briefcase',
  },
  {
    id: 'packages',
    label: 'Packages',
    path: '/packages',
    icon: 'icon-present',
  },
  {
    id: 'features',
    label: 'Features',
    path: '/features',
    icon: 'icon-star',
  },
  {
    id: 'certificates',
    label: 'Certificates',
    path: '/certificates',
    icon: 'icon-badge',
  },
  {
    id: 'items',
    label: 'Items Repository',
    path: '/items',
    icon: 'icon-folder',
  },
  {
    id: 'finance',
    label: 'Finance',
    path: '/finance',
    icon: 'icon-wallet',
  },
  {
    id: 'users',
    label: 'Users',
    path: '/users',
    icon: 'icon-people',
  },
  {
    id: 'roles',
    label: 'User Roles',
    path: '/roles',
    icon: 'icon-shield',
  },

  // SETTINGS Section
  {
    id: 'settings-section',
    label: 'SETTINGS',
    isSection: true,
  },
  {
    id: 'profile',
    label: 'My Profile',
    path: '/profile',
    icon: 'icon-user',
  },
  {
    id: 'audit-logs',
    label: 'Audit Logs',
    path: '/audit-logs',
    icon: 'icon-shield',
  },
  {
    id: 'settings',
    label: 'Settings',
    path: '/settings',
    icon: 'icon-settings',
  },
]

/**
 * Navigation configuration for Company Admin role
 * Full company-level access with user/project management
 * Includes permission checks for granular access control
 */
const COMPANY_ADMIN_NAVIGATION = [
  // MAIN Section
  {
    id: 'main-section',
    label: 'MAIN',
    isSection: true,
  },
  {
    id: 'dashboard',
    label: 'Dashboard',
    path: '/dashboard',
    icon: 'icon-grid',
    exact: true,
    permission: 'dashboard.view',
  },
  {
    id: 'compliance-dashboard',
    label: 'Compliance Dashboard',
    path: '/compliance-dashboard',
    icon: 'icon-chart',
  },

  // PROJECTS Section
  {
    id: 'projects-section',
    label: 'PROJECTS',
    isSection: true,
  },
  {
    id: 'projects',
    label: 'All Projects',
    path: '/projects',
    icon: 'icon-folder',
    permission: 'projects.view',
  },
  {
    id: 'workflows',
    label: 'Workflows',
    path: '/workflows',
    icon: 'icon-layers',
    permission: 'workflows.view',
  },
  // TEAM Section
  {
    id: 'team-section',
    label: 'TEAM',
    isSection: true,
  },
  {
    id: 'team-members',
    label: 'Team Members',
    path: '/team-members',
    icon: 'icon-people',
    permission: 'users.view',
  },
  {
    id: 'company-roles',
    label: 'Roles',
    path: '/company-roles',
    icon: 'icon-shield',
    permission: 'roles.view',
  },
  {
    id: 'my-tasks',
    label: 'My Tasks',
    path: '/my-tasks',
    icon: 'icon-note',
    permission: 'projects.view',
  },

  // RESOURCES Section
  {
    id: 'resources-section',
    label: 'RESOURCES',
    isSection: true,
  },
  {
    id: 'resources',
    label: 'Resources',
    path: '/resources',
    icon: 'icon-docs',
  },

  // REPORTS Section
  {
    id: 'reports-section',
    label: 'REPORTS',
    isSection: true,
  },
  {
    id: 'reports',
    label: 'Reports',
    path: '/reports',
    icon: 'icon-chart',
    permission: 'reports.view',
  },
  {
    id: 'audit-logs',
    label: 'Audit Logs',
    path: '/audit-logs',
    icon: 'icon-shield',
    permission: 'audit_logs.view',
  },

  // SETTINGS Section
  {
    id: 'settings-section',
    label: 'SETTINGS',
    isSection: true,
  },
  {
    id: 'profile',
    label: 'My Profile',
    path: '/profile',
    icon: 'icon-user',
  },
  {
    id: 'company-settings',
    label: 'Company Settings',
    path: '/company-settings',
    icon: 'icon-settings',
    permission: 'settings.view',
  },
]

/**
 * Navigation configuration for Project Manager role
 * Project-level access with team and task management
 * Includes permission checks for granular access control
 */
const PROJECT_MANAGER_NAVIGATION = [
  // MAIN Section
  {
    id: 'main-section',
    label: 'MAIN',
    isSection: true,
  },
  {
    id: 'dashboard',
    label: 'Dashboard',
    path: '/dashboard',
    icon: 'icon-grid',
    exact: true,
    permission: 'dashboard.view',
  },
  {
    id: 'compliance-dashboard',
    label: 'Compliance Dashboard',
    path: '/compliance-dashboard',
    icon: 'icon-chart',
  },

  // PROJECTS Section
  {
    id: 'projects-section',
    label: 'PROJECTS',
    isSection: true,
  },
  {
    id: 'projects',
    label: 'My Projects',
    path: '/projects',
    icon: 'icon-folder',
    permission: 'projects.view',
  },
  {
    id: 'my-tasks',
    label: 'My Tasks',
    path: '/my-tasks',
    icon: 'icon-note',
    permission: 'projects.view',
  },
  {
    id: 'checklists',
    label: 'Checklists',
    path: '/checklists',
    icon: 'icon-list',
    permission: 'checklists.view',
  },

  // TEAM Section
  {
    id: 'team-section',
    label: 'TEAM',
    isSection: true,
  },
  {
    id: 'team',
    label: 'Team Members',
    path: '/team',
    icon: 'icon-people',
    permission: 'users.view',
  },

  // RESOURCES Section
  {
    id: 'resources-section',
    label: 'RESOURCES',
    isSection: true,
  },
  {
    id: 'resources',
    label: 'Resources',
    path: '/resources',
    icon: 'icon-docs',
  },

  // REPORTS Section
  {
    id: 'reports-section',
    label: 'REPORTS',
    isSection: true,
  },
  {
    id: 'reports',
    label: 'My Reports',
    path: '/my-reports',
    icon: 'icon-chart',
    permission: 'reports.view',
  },

  // SETTINGS Section
  {
    id: 'settings-section',
    label: 'SETTINGS',
    isSection: true,
  },
  {
    id: 'profile',
    label: 'My Profile',
    path: '/profile',
    icon: 'icon-user',
  },
]

/**
 * Navigation configuration for Management role
 * View assigned projects and manage own tasks
 * Includes permission checks for granular access control
 */
const MANAGEMENT_NAVIGATION = [
  // MAIN Section
  {
    id: 'main-section',
    label: 'MAIN',
    isSection: true,
  },
  {
    id: 'dashboard',
    label: 'Dashboard',
    path: '/dashboard',
    icon: 'icon-grid',
    exact: true,
    permission: 'dashboard.view',
  },
  {
    id: 'compliance-dashboard',
    label: 'Compliance Dashboard',
    path: '/compliance-dashboard',
    icon: 'icon-chart',
  },

  // WORK Section
  {
    id: 'work-section',
    label: 'WORK',
    isSection: true,
  },
  {
    id: 'projects',
    label: 'Assigned Projects',
    path: '/projects',
    icon: 'icon-folder',
    permission: 'projects.view',
  },
  {
    id: 'my-tasks',
    label: 'My Tasks',
    path: '/my-tasks',
    icon: 'icon-note',
    permission: 'projects.view',
  },

  // RESOURCES Section
  {
    id: 'resources-section',
    label: 'RESOURCES',
    isSection: true,
  },
  {
    id: 'resources',
    label: 'Resources',
    path: '/resources',
    icon: 'icon-docs',
  },

  // REPORTS Section
  {
    id: 'reports-section',
    label: 'REPORTS',
    isSection: true,
  },
  {
    id: 'reports',
    label: 'Reports',
    path: '/reports',
    icon: 'icon-chart',
    permission: 'reports.view',
  },

  // SETTINGS Section
  {
    id: 'settings-section',
    label: 'SETTINGS',
    isSection: true,
  },
  {
    id: 'profile',
    label: 'My Profile',
    path: '/profile',
    icon: 'icon-user',
  },
]

/**
 * Navigation configuration for Employee role
 * Basic access - own tasks and certificates only
 * Includes permission checks for granular access control
 */
const EMPLOYEE_NAVIGATION = [
  // MAIN Section
  {
    id: 'main-section',
    label: 'MAIN',
    isSection: true,
  },
  {
    id: 'dashboard',
    label: 'Dashboard',
    path: '/dashboard',
    icon: 'icon-grid',
    exact: true,
    permission: 'dashboard.view',
  },

  // WORK Section
  {
    id: 'work-section',
    label: 'WORK',
    isSection: true,
  },
  {
    id: 'my-tasks',
    label: 'My Tasks',
    path: '/my-tasks',
    icon: 'icon-note',
    permission: 'projects.view',
  },

  // RESOURCES Section
  {
    id: 'resources-section',
    label: 'RESOURCES',
    isSection: true,
  },
  {
    id: 'resources',
    label: 'Resources',
    path: '/resources',
    icon: 'icon-docs',
  },

  // SETTINGS Section
  {
    id: 'settings-section',
    label: 'SETTINGS',
    isSection: true,
  },
  {
    id: 'profile',
    label: 'My Profile',
    path: '/profile',
    icon: 'icon-user',
    permission: 'settings.view',
  },
]

/**
 * Navigation configuration for Compliance Manager role
 * Similar to Project Manager but with compliance focus
 */
const COMPLIANCE_MANAGER_NAVIGATION = [
  // MAIN Section
  {
    id: 'main-section',
    label: 'MAIN',
    isSection: true,
  },
  {
    id: 'dashboard',
    label: 'Dashboard',
    path: '/dashboard',
    icon: 'icon-grid',
    exact: true,
    permission: 'dashboard.view',
  },
  {
    id: 'compliance-dashboard',
    label: 'Compliance Dashboard',
    path: '/compliance-dashboard',
    icon: 'icon-chart',
  },

  // PROJECTS Section
  {
    id: 'projects-section',
    label: 'PROJECTS',
    isSection: true,
  },
  {
    id: 'projects',
    label: 'All Projects',
    path: '/projects',
    icon: 'icon-folder',
    permission: 'projects.view',
  },
  {
    id: 'workflows',
    label: 'Workflows',
    path: '/workflows',
    icon: 'icon-refresh',
  },

  // TEAM Section
  {
    id: 'team-section',
    label: 'TEAM',
    isSection: true,
  },
  {
    id: 'team-members',
    label: 'Team Members',
    path: '/team-members',
    icon: 'icon-people',
  },
  {
    id: 'company-roles',
    label: 'Roles',
    path: '/company-roles',
    icon: 'icon-shield',
  },
  {
    id: 'my-tasks',
    label: 'My Tasks',
    path: '/my-tasks',
    icon: 'icon-note',
    permission: 'projects.view',
  },

  // RESOURCES Section
  {
    id: 'resources-section',
    label: 'RESOURCES',
    isSection: true,
  },
  {
    id: 'resources',
    label: 'Resources',
    path: '/resources',
    icon: 'icon-docs',
  },

  // REPORTS Section
  {
    id: 'reports-section',
    label: 'REPORTS',
    isSection: true,
  },
  {
    id: 'reports',
    label: 'Reports',
    path: '/reports',
    icon: 'icon-chart',
    permission: 'reports.view',
  },
  {
    id: 'audit-logs',
    label: 'Audit Logs',
    path: '/audit-logs',
    icon: 'icon-doc',
  },

  // SETTINGS Section
  {
    id: 'settings-section',
    label: 'SETTINGS',
    isSection: true,
  },
  {
    id: 'profile',
    label: 'My Profile',
    path: '/profile',
    icon: 'icon-user',
    permission: 'settings.view',
  },
]

/**
 * Navigation configuration for Executive role
 * High-level view focused on compliance and reports
 */
const EXECUTIVE_NAVIGATION = [
  // MAIN Section
  {
    id: 'main-section',
    label: 'MAIN',
    isSection: true,
  },
  {
    id: 'dashboard',
    label: 'Dashboard',
    path: '/dashboard',
    icon: 'icon-grid',
    exact: true,
    permission: 'dashboard.view',
  },
  {
    id: 'compliance-dashboard',
    label: 'Compliance Dashboard',
    path: '/compliance-dashboard',
    icon: 'icon-chart',
  },

  // PROJECTS Section
  {
    id: 'projects-section',
    label: 'PROJECTS',
    isSection: true,
  },
  {
    id: 'projects',
    label: 'All Projects',
    path: '/projects',
    icon: 'icon-folder',
    permission: 'projects.view',
  },

  // RESOURCES Section
  {
    id: 'resources-section',
    label: 'RESOURCES',
    isSection: true,
  },
  {
    id: 'resources',
    label: 'Resources',
    path: '/resources',
    icon: 'icon-docs',
  },

  // REPORTS Section
  {
    id: 'reports-section',
    label: 'REPORTS',
    isSection: true,
  },
  {
    id: 'reports',
    label: 'Reports',
    path: '/reports',
    icon: 'icon-chart',
    permission: 'reports.view',
  },

  // SETTINGS Section
  {
    id: 'settings-section',
    label: 'SETTINGS',
    isSection: true,
  },
  {
    id: 'profile',
    label: 'My Profile',
    path: '/profile',
    icon: 'icon-user',
    permission: 'settings.view',
  },
]

/**
 * Role-to-Navigation mapping
 */
export const NAVIGATION_BY_ROLE = {
  [ROLES.SUPER_ADMIN]: SUPER_ADMIN_NAVIGATION,
  [ROLES.COMPANY_ADMIN]: COMPANY_ADMIN_NAVIGATION,
  [ROLES.COMPLIANCE_MANAGER]: COMPLIANCE_MANAGER_NAVIGATION,
  [ROLES.PROJECT_MANAGER]: PROJECT_MANAGER_NAVIGATION,
  [ROLES.MANAGEMENT]: MANAGEMENT_NAVIGATION,
  [ROLES.EXECUTIVE]: EXECUTIVE_NAVIGATION,
  [ROLES.EMPLOYEE]: EMPLOYEE_NAVIGATION,
}

/**
 * Helper function to get navigation items for a specific role
 * @param {string} role - User role
 * @returns {Array} Navigation items array
 */
export const getNavigationByRole = (role) => {
  return NAVIGATION_BY_ROLE[role] || []
}

/**
 * Helper function to check if a navigation item is active
 * @param {string} currentPath - Current route path
 * @param {Object} navItem - Navigation item object
 * @returns {boolean} True if navigation item is active
 */
export const isNavItemActive = (currentPath, navItem) => {
  if (navItem.exact) {
    return currentPath === navItem.path
  }
  return currentPath.startsWith(navItem.path)
}

/**
 * Helper function to check if a submenu contains active item
 * @param {string} currentPath - Current route path
 * @param {Array} submenu - Submenu items array
 * @returns {boolean} True if any submenu item is active
 */
export const hasActiveSubmenuItem = (currentPath, submenu = []) => {
  return submenu.some(item => isNavItemActive(currentPath, item))
}

/**
 * Helper function to flatten navigation tree (useful for breadcrumbs)
 * @param {Array} navigation - Navigation array
 * @returns {Array} Flattened navigation items
 */
export const flattenNavigation = (navigation) => {
  const flattened = []

  const flatten = (items) => {
    items.forEach(item => {
      if (item.path) {
        flattened.push(item)
      }
      if (item.submenu) {
        flatten(item.submenu)
      }
    })
  }

  flatten(navigation)
  return flattened
}

/**
 * Helper function to find navigation item by path
 * @param {Array} navigation - Navigation array
 * @param {string} path - Route path
 * @returns {Object|null} Navigation item or null
 */
export const findNavItemByPath = (navigation, path) => {
  for (const item of navigation) {
    if (item.path === path) {
      return item
    }
    if (item.submenu) {
      const found = findNavItemByPath(item.submenu, path)
      if (found) return found
    }
  }
  return null
}

/**
 * Helper function to build breadcrumbs from current path
 * @param {Array} navigation - Navigation array
 * @param {string} currentPath - Current route path
 * @returns {Array} Breadcrumb items array
 */
export const getBreadcrumbs = (navigation, currentPath) => {
  const breadcrumbs = []

  const findPath = (items, path, parents = []) => {
    for (const item of items) {
      if (item.path === path) {
        return [...parents, item]
      }
      if (item.submenu) {
        const found = findPath(item.submenu, path, [...parents, item])
        if (found) return found
      }
    }
    return null
  }

  const path = findPath(navigation, currentPath)
  return path || breadcrumbs
}

/**
 * Helper function to check if user has permission for a navigation item
 * @param {Object} navItem - Navigation item
 * @param {Function} checkPermission - Permission check function (from usePermissions hook)
 * @returns {boolean} True if user has required permission(s)
 */
const hasNavItemPermission = (navItem, checkPermission) => {
  // Section headers are always visible
  if (navItem.isSection) {
    return true
  }

  // No permission required - always visible
  if (!navItem.permission && !navItem.permissions) {
    return true
  }

  // Single permission check
  if (navItem.permission) {
    return checkPermission(navItem.permission)
  }

  // Multiple permissions check
  if (navItem.permissions && Array.isArray(navItem.permissions)) {
    if (navItem.requireAllPermissions) {
      // Require ALL permissions
      return navItem.permissions.every(p => checkPermission(p))
    }
    // Require ANY permission
    return navItem.permissions.some(p => checkPermission(p))
  }

  return true
}

/**
 * Filter navigation items based on user permissions
 * Removes items the user doesn't have permission to see
 * Also removes section headers if all items in section are hidden
 *
 * @param {Array} navigation - Navigation items array
 * @param {Function} checkPermission - Permission check function (from usePermissions hook)
 * @returns {Array} Filtered navigation items
 */
export const filterNavigationByPermissions = (navigation, checkPermission) => {
  if (!navigation || !checkPermission) {
    return navigation || []
  }

  // First pass: filter items by permission
  const filteredItems = navigation.filter(item => {
    // Check submenu items if present
    if (item.submenu) {
      const filteredSubmenu = item.submenu.filter(subItem =>
        hasNavItemPermission(subItem, checkPermission)
      )
      // Hide parent if all submenu items are hidden
      if (filteredSubmenu.length === 0) {
        return false
      }
      item.submenu = filteredSubmenu
    }

    return hasNavItemPermission(item, checkPermission)
  })

  // Second pass: remove empty sections
  // A section is empty if there are no non-section items following it before the next section
  const result = []
  let currentSection = null
  let sectionHasItems = false

  for (let i = 0; i < filteredItems.length; i++) {
    const item = filteredItems[i]

    if (item.isSection) {
      // If we have a previous section with items, add it to result
      if (currentSection && sectionHasItems) {
        result.push(currentSection)
      }
      // Start tracking new section
      currentSection = item
      sectionHasItems = false
    } else {
      // This is a regular item
      sectionHasItems = true
      // If there's a pending section, add it first
      if (currentSection) {
        result.push(currentSection)
        currentSection = null
      }
      result.push(item)
    }
  }

  // Don't forget the last section if it has items
  if (currentSection && sectionHasItems) {
    result.push(currentSection)
  }

  return result
}

/**
 * Get filtered navigation for a role with permission checks
 * Combines role-based navigation with permission filtering
 *
 * @param {string} role - User role
 * @param {Function} checkPermission - Permission check function (from usePermissions hook)
 * @returns {Array} Filtered navigation items
 */
export const getFilteredNavigationByRole = (role, checkPermission) => {
  const navigation = getNavigationByRole(role)
  return filterNavigationByPermissions(navigation, checkPermission)
}

/**
 * Export default configuration - Direct export of NAVIGATION_BY_ROLE for DashboardLayout
 */
export default NAVIGATION_BY_ROLE
